import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { clearSceneDesignsForTemplates } from 'js/src/data/templates/templates-actions';
import type { RootState } from 'js/src/libs/RootState';
import type { SceneSequenceDesign } from 'js/src/luminary/scene-sequence-designs';

type LoadingStatus = 'idle' | 'loading' | 'loaded' | 'failed';

type SceneSequenceDesignsState = {
    byTemplateId: Record<string, SceneSequenceDesign[]>;
    loadingStatus: Record<string, LoadingStatus>;
};

const initialState: SceneSequenceDesignsState = {
    byTemplateId: {},
    loadingStatus: {},
};

const sceneSequenceDesignsSlice = createSlice({
    name: 'sceneSequenceDesigns',
    initialState,
    reducers: {
        setSceneSequenceDesigns: (
            state,
            action: PayloadAction<{ templateId: string; designs: SceneSequenceDesign[] }>,
        ) => {
            const { templateId, designs } = action.payload;
            state.byTemplateId[templateId] = designs;
            state.loadingStatus[templateId] = 'loaded';
        },
        setSceneSequenceDesignsLoading: (state, action: PayloadAction<{ templateId: string }>) => {
            state.loadingStatus[action.payload.templateId] = 'loading';
        },
        setSceneSequenceDesignsFailed: (state, action: PayloadAction<{ templateId: string }>) => {
            state.loadingStatus[action.payload.templateId] = 'failed';
        },
    },
    extraReducers: (builder) => {
        builder.addCase(clearSceneDesignsForTemplates, (state, action) => {
            const { templateIds } = action.payload;
            for (const templateId of templateIds) {
                delete state.byTemplateId[templateId];
                delete state.loadingStatus[templateId];
            }
        });
    },
    selectors: {
        selectSceneSequenceDesigns: (state, templateId: string): SceneSequenceDesign[] => {
            return state.byTemplateId[templateId] ?? [];
        },
        selectSceneSequenceDesignsLoadingStatus: (state, templateId: string): LoadingStatus => {
            return state.loadingStatus[templateId] ?? 'idle';
        },
    },
});

export const {
    setSceneSequenceDesigns,
    setSceneSequenceDesignsLoading,
    setSceneSequenceDesignsFailed,
} = sceneSequenceDesignsSlice.actions;

export const { selectSceneSequenceDesigns, selectSceneSequenceDesignsLoadingStatus } =
    sceneSequenceDesignsSlice.getSelectors(
        (state: RootState) => state.templates.sceneSequenceDesigns,
    );

export default sceneSequenceDesignsSlice;
